import React, { useState } from 'react';
import { login, loginEmail, loginSelectAccount, forgotPassword, verifyOTP } from '../api';
import logo from '../logo.png';

function useIsMobile() {
  const [isMobile] = useState(window.innerWidth < 768);
  return isMobile;
}

// ─── ACCOUNT SELECTOR ────────────────────────────────────────────────────────
function AccountSelector({ accounts, email, password, onLogin, onBack }) {
  const [selected, setSelected]   = useState('');
  const [loading,  setLoading]    = useState(false);
  const [error,    setError]      = useState('');

  const handleSelect = () => {
    if (!selected) { setError('Please select an account'); return; }
    setLoading(true);
    loginSelectAccount(email, password, parseInt(selected))
      .then(res => {
        // Pass email and all accounts so header can show Switch Account
        onLogin({ ...res.data, email, accounts });
      })
      .catch(err => {
        setError(err.response?.data?.detail || 'Login failed');
        setLoading(false);
      });
  };

  return (
    <div>
      <div style={{ fontSize:20, fontWeight:700, color:'#1a1a1a', marginBottom:4 }}>Select Account</div>
      <div style={{ fontSize:13, color:'#777', marginBottom:24 }}>
        Multiple accounts found for <strong>{email}</strong>
      </div>
      {error && <div style={{ background:'#fdf0f0', border:'1px solid #f5c6c6', borderRadius:8, padding:'10px 14px', fontSize:13, color:'#c0392b', marginBottom:16 }}>{error}</div>}
      <label style={{ fontSize:12, color:'#444', fontWeight:600, marginBottom:8, display:'block' }}>Choose your account</label>
      <select
        value={selected}
        onChange={e => setSelected(e.target.value)}
        style={{ width:'100%', padding:'12px 16px', border:'1px solid #e0d8d5', borderRadius:8, fontSize:14, outline:'none', marginBottom:20, color:'#333', background:'#fdfcfc', boxSizing:'border-box' }}
      >
        <option value="">— Select account —</option>
        {accounts.map(a => (
          <option key={a.client_code} value={a.client_code}>
            {a.client_code} — {a.name}
          </option>
        ))}
      </select>
      <button onClick={handleSelect} disabled={loading}
        style={{ width:'100%', padding:13, background:'#42000B', color:'#fff', border:'none', borderRadius:8, fontSize:14, fontWeight:600, cursor:loading?'not-allowed':'pointer', opacity:loading?0.8:1 }}>
        {loading ? 'Logging in...' : 'Continue'}
      </button>
      <button onClick={onBack}
        style={{ width:'100%', padding:10, marginTop:10, background:'transparent', color:'#888', border:'1px solid #e0d8d5', borderRadius:8, fontSize:13, cursor:'pointer' }}>
        ← Back
      </button>
    </div>
  );
}

// ─── FORGOT PASSWORD ──────────────────────────────────────────────────────────
function ForgotPassword({ onBack }) {
  const [step,        setStep]        = useState('email'); // email → otp → done
  const [email,       setEmail]       = useState('');
  const [otp,         setOtp]         = useState('');
  const [newPw,       setNewPw]       = useState('');
  const [confirmPw,   setConfirmPw]   = useState('');
  const [loading,     setLoading]     = useState(false);
  const [error,       setError]       = useState('');
  const [message,     setMessage]     = useState('');

  const handleSendOTP = () => {
    if (!email) { setError('Please enter your email'); return; }
    setLoading(true); setError('');
    forgotPassword(email)
      .then(res => { setMessage(res.data.message); setStep('otp'); setLoading(false); })
      .catch(err => { setError(err.response?.data?.detail || 'Failed to send OTP'); setLoading(false); });
  };

  const handleVerifyOTP = () => {
    if (!otp || !newPw || !confirmPw) { setError('All fields required'); return; }
    if (newPw !== confirmPw) { setError('Passwords do not match'); return; }
    if (newPw.length < 8) { setError('Password must be at least 8 characters'); return; }
    setLoading(true); setError('');
    verifyOTP(email, otp, newPw)
      .then(() => { setStep('done'); setLoading(false); })
      .catch(err => { setError(err.response?.data?.detail || 'Invalid OTP'); setLoading(false); });
  };

  const inputStyle = { width:'100%', padding:'12px 16px', border:'1px solid #e0d8d5', borderRadius:8, fontSize:14, outline:'none', marginBottom:16, color:'#333', background:'#fdfcfc', boxSizing:'border-box' };
  const labelStyle = { fontSize:12, color:'#444', fontWeight:600, marginBottom:6, display:'block' };

  if (step === 'done') return (
    <div style={{ textAlign:'center' }}>
      <div style={{ fontSize:40, marginBottom:16 }}>✅</div>
      <div style={{ fontSize:18, fontWeight:700, color:'#1a1a1a', marginBottom:8 }}>Password Reset!</div>
      <div style={{ fontSize:13, color:'#777', marginBottom:24 }}>Your password has been reset successfully.</div>
      <button onClick={onBack}
        style={{ width:'100%', padding:13, background:'#42000B', color:'#fff', border:'none', borderRadius:8, fontSize:14, fontWeight:600, cursor:'pointer' }}>
        Back to Login
      </button>
    </div>
  );

  return (
    <div>
      <div style={{ fontSize:20, fontWeight:700, color:'#1a1a1a', marginBottom:4 }}>
        {step === 'email' ? 'Forgot Password' : 'Enter OTP'}
      </div>
      <div style={{ fontSize:13, color:'#777', marginBottom:24 }}>
        {step === 'email' ? 'Enter your registered email to receive an OTP.' : `OTP sent to ${email}. Check your inbox.`}
      </div>

      {error && <div style={{ background:'#fdf0f0', border:'1px solid #f5c6c6', borderRadius:8, padding:'10px 14px', fontSize:13, color:'#c0392b', marginBottom:16 }}>{error}</div>}
      {message && step === 'otp' && <div style={{ background:'#f0fdf4', border:'1px solid #86efac', borderRadius:8, padding:'10px 14px', fontSize:13, color:'#166534', marginBottom:16 }}>{message}</div>}

      {step === 'email' && (
        <>
          <label style={labelStyle}>Email Address</label>
          <input style={inputStyle} type="email" placeholder="your@email.com" value={email}
            onChange={e => setEmail(e.target.value)}
            onFocus={e => e.target.style.borderColor='#42000B'}
            onBlur={e => e.target.style.borderColor='#e0d8d5'}
          />
          <button onClick={handleSendOTP} disabled={loading}
            style={{ width:'100%', padding:13, background:'#42000B', color:'#fff', border:'none', borderRadius:8, fontSize:14, fontWeight:600, cursor:loading?'not-allowed':'pointer', opacity:loading?0.8:1 }}>
            {loading ? 'Sending OTP...' : 'Send OTP'}
          </button>
        </>
      )}

      {step === 'otp' && (
        <>
          <label style={labelStyle}>6-digit OTP</label>
          <input style={inputStyle} type="text" inputMode="numeric" placeholder="Enter OTP" maxLength={6} value={otp}
            onChange={e => setOtp(e.target.value)}
            onFocus={e => e.target.style.borderColor='#42000B'}
            onBlur={e => e.target.style.borderColor='#e0d8d5'}
          />
          <label style={labelStyle}>New Password</label>
          <input style={inputStyle} type="password" placeholder="Min 8 characters" value={newPw}
            onChange={e => setNewPw(e.target.value)}
            onFocus={e => e.target.style.borderColor='#42000B'}
            onBlur={e => e.target.style.borderColor='#e0d8d5'}
          />
          <label style={labelStyle}>Confirm New Password</label>
          <input style={inputStyle} type="password" placeholder="Repeat password" value={confirmPw}
            onChange={e => setConfirmPw(e.target.value)}
            onFocus={e => e.target.style.borderColor='#42000B'}
            onBlur={e => e.target.style.borderColor='#e0d8d5'}
          />
          <button onClick={handleVerifyOTP} disabled={loading}
            style={{ width:'100%', padding:13, background:'#42000B', color:'#fff', border:'none', borderRadius:8, fontSize:14, fontWeight:600, cursor:loading?'not-allowed':'pointer', opacity:loading?0.8:1 }}>
            {loading ? 'Resetting...' : 'Reset Password'}
          </button>
          <button onClick={() => { setStep('email'); setOtp(''); setNewPw(''); setConfirmPw(''); setError(''); }}
            style={{ width:'100%', padding:10, marginTop:10, background:'transparent', color:'#888', border:'1px solid #e0d8d5', borderRadius:8, fontSize:13, cursor:'pointer' }}>
            Resend OTP
          </button>
        </>
      )}

      <button onClick={onBack}
        style={{ width:'100%', padding:10, marginTop:10, background:'transparent', color:'#888', border:'none', fontSize:13, cursor:'pointer' }}>
        ← Back to Login
      </button>
    </div>
  );
}

// ─── MAIN LOGIN ───────────────────────────────────────────────────────────────
export default function Login({ onLogin }) {
  const [identifier,  setIdentifier]  = useState(''); // email or client code
  const [password,    setPassword]    = useState('');
  const [error,       setError]       = useState('');
  const [loading,     setLoading]     = useState(false);
  const [screen,      setScreen]      = useState('login'); // login | selector | forgot
  const [accounts,    setAccounts]    = useState([]);
  const isMobile = useIsMobile();

  const isEmail = identifier.includes('@');

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!identifier || !password) { setError('Please enter your credentials'); return; }
    setLoading(true);

    if (isEmail) {
      loginEmail(identifier, password)
        .then(res => {
          if (res.data.multiple_accounts) {
            setAccounts(res.data.accounts);
            setScreen('selector');
            setLoading(false);
          } else {
            // Single account email login - pass email and accounts
            onLogin({ ...res.data, email: identifier, accounts: [] });
          }
        })
        .catch(err => {
          setError(err.response?.data?.detail || 'Login failed. Please try again.');
          setLoading(false);
        });
    } else {
      login(identifier, password)
        .then(res => onLogin(res.data))
        .catch(err => {
          setError(err.response?.data?.detail || 'Login failed. Please try again.');
          setLoading(false);
        });
    }
  };

  const inputStyle = { width:'100%', padding:'12px 16px', border:'1px solid #e0d8d5', borderRadius:8, fontSize:14, outline:'none', marginBottom:16, color:'#333', background:'#fdfcfc', boxSizing:'border-box' };
  const labelStyle = { fontSize:12, color:'#444', fontWeight:600, marginBottom:6, display:'block' };

  const renderCard = () => {
    if (screen === 'selector') return (
      <AccountSelector
        accounts={accounts} email={identifier} password={password}
        onLogin={onLogin} onBack={() => setScreen('login')}
      />
    );
    if (screen === 'forgot') return (
      <ForgotPassword onBack={() => setScreen('login')} />
    );
    return (
      <>
        <div style={{ fontSize:22, fontWeight:700, color:'#1a1a1a', marginBottom:4 }}>Client Login</div>
        <div style={{ fontSize:13, color:'#777', marginBottom:20 }}>PCSPL Pledge+ Portal</div>

        {error && (
          <div style={{ background:'#fdf0f0', border:'1px solid #f5c6c6', borderRadius:8, padding:'12px 16px', fontSize:13, color:'#c0392b', marginBottom:16, lineHeight:1.5 }}>
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} autoComplete="off">
          <label style={labelStyle}>
            {isEmail ? 'Email Address' : identifier ? 'Client Code' : 'Email or Client Code'}
          </label>
          <input style={inputStyle}
            type="text" inputMode={isEmail ? 'email' : 'numeric'}
            placeholder="Email or Client Code" value={identifier}
            onChange={e => setIdentifier(e.target.value)}
            onFocus={e => e.target.style.borderColor='#42000B'}
            onBlur={e => e.target.style.borderColor='#e0d8d5'}
          />
          <label style={labelStyle}>Password</label>
          <input style={inputStyle}
            type="password" placeholder="Enter your password" value={password}
            onChange={e => setPassword(e.target.value)}
            onFocus={e => e.target.style.borderColor='#42000B'}
            onBlur={e => e.target.style.borderColor='#e0d8d5'}
          />
          <button type="submit" disabled={loading}
            style={{ width:'100%', padding:13, background:'#42000B', color:'#fff', border:'none', borderRadius:8, fontSize:14, fontWeight:600, cursor:loading?'not-allowed':'pointer', opacity:loading?0.8:1, marginTop:4 }}>
            {loading ? 'Logging in...' : 'Login'}
          </button>
        </form>

        <div style={{ textAlign:'center', marginTop:14 }}>
          <button onClick={() => { setScreen('forgot'); setError(''); }}
            style={{ background:'none', border:'none', color:'#6B1C2A', fontSize:12, cursor:'pointer', textDecoration:'underline' }}>
            Forgot Password?
          </button>
        </div>
      </>
    );
  };

  // ── MOBILE ─────────────────────────────────────────────────────────────────
  if (isMobile) return (
    <div style={{ minHeight:'100vh', background:'#42000B', display:'flex', flexDirection:'column', fontFamily:'system-ui,-apple-system,sans-serif' }}>
      <div style={{ padding:'40px 24px 32px', display:'flex', flexDirection:'column', alignItems:'center' }}>
        <img src={logo} alt="Pashupati Capital" style={{ height:48, marginBottom:28 }} />
        <div style={{ background:'#fff', borderRadius:16, padding:'28px 24px', width:'100%', maxWidth:400, boxShadow:'0 10px 40px rgba(0,0,0,0.2)' }}>
          {renderCard()}
        </div>
      </div>
      <div style={{ flex:1, padding:'24px 24px 40px', color:'#fff' }}>
        <div style={{ fontSize:22, fontWeight:700, marginBottom:12 }}>PCSPL Pledge+</div>
        <div style={{ fontSize:15, color:'rgba(255,255,255,0.85)', marginBottom:12, lineHeight:1.5 }}>
          Pledge your shares/MFs as collateral to receive margin, then trade using defined algo strategies.
        </div>
        <ul style={{ listStyle:'none', padding:0, margin:'0 0 24px 0' }}>
          {[['Margin against pledged shares/MFs','based on transparent haircuts'],['Flexible tenure','from 30 days to 1 year'],['Transparent P&L dashboard','with real-time tracking']].map(([b,r]) => (
            <li key={b} style={{ fontSize:14, marginBottom:12, display:'flex', gap:10 }}>
              <span style={{ color:'#ff4d6d', fontWeight:'bold' }}>•</span>
              <div><strong style={{ color:'#ff4d6d' }}>{b}</strong> {r}</div>
            </li>
          ))}
        </ul>
        <div style={{ borderTop:'1px solid rgba(255,255,255,0.15)', paddingTop:20, display:'flex', gap:32, fontSize:13, color:'rgba(255,255,255,0.7)' }}>
          <div><div style={{ fontWeight:600, color:'#fff', marginBottom:3 }}>Contact us</div><div>(91-22) 40345500</div></div>
          <div><div style={{ fontWeight:600, color:'#fff', marginBottom:3 }}>Email</div><div>info@pashupaticapital.com</div></div>
        </div>
        <div style={{ fontSize:11, color:'rgba(255,255,255,0.4)', marginTop:16, lineHeight:1.6, padding:'0 4px' }}>
          Trading and margin carry risk of loss, including loss of pledged securities in the event of a margin shortfall. Past performance is not indicative of future results and returns are not guaranteed.
        </div>
      </div>
    </div>
  );

  // ── DESKTOP ────────────────────────────────────────────────────────────────
  return (
    <div style={{ minHeight:'100vh', display:'grid', gridTemplateColumns:'1.1fr 0.9fr', fontFamily:'system-ui,-apple-system,sans-serif' }}>
      <div style={{ background:'#42000B', padding:'60px 80px', display:'flex', flexDirection:'column', justifyContent:'space-between', color:'#fff', overflow:'hidden' }}>
        <img src={logo} alt="Pashupati Capital" style={{ height:52, alignSelf:'flex-start' }} />
        <div style={{ maxWidth:520, margin:'40px 0' }}>
          <div style={{ fontSize:36, fontWeight:700, marginBottom:24, letterSpacing:'-0.02em' }}>PCSPL Pledge+</div>
          <div style={{ fontSize:18, color:'rgba(255,255,255,0.9)', marginBottom:28, lineHeight:1.4 }}>
            Pledge your shares/MFs as collateral to receive margin, then trade using defined algo strategies.
          </div>
          <ul style={{ listStyle:'none', padding:0, margin:0 }}>
            {[['Margin against pledged shares/MFs','based on transparent haircuts'],['Flexible tenure','from 30 days to 1 year'],['Transparent P&L dashboard','with real-time tracking']].map(([b,r]) => (
              <li key={b} style={{ fontSize:15, marginBottom:14, display:'flex', alignItems:'baseline', gap:12 }}>
                <span style={{ color:'#ff4d6d', fontSize:18, fontWeight:'bold', flexShrink:0 }}>•</span>
                <div><strong style={{ color:'#ff4d6d' }}>{b}</strong> {r}</div>
              </li>
            ))}
          </ul>
        </div>
        <div style={{ display:'flex', gap:40, borderTop:'1px solid rgba(255,255,255,0.15)', paddingTop:24, fontSize:13, color:'rgba(255,255,255,0.7)' }}>
          <div style={{ display:'flex', flexDirection:'column', gap:4 }}><span style={{ fontWeight:600, color:'#fff' }}>Contact us</span><span>(91-22) 40345500</span></div>
          <div style={{ display:'flex', flexDirection:'column', gap:4 }}><span style={{ fontWeight:600, color:'#fff' }}>Email</span><span>info@pashupaticapital.com</span></div>
        </div>
        <div style={{ fontSize:11, color:'rgba(255,255,255,0.4)', marginTop:16, lineHeight:1.6 }}>
          Trading and margin carry risk of loss, including loss of pledged securities in the event of a margin shortfall. Past performance is not indicative of future results and returns are not guaranteed.
        </div>
      </div>
      <div style={{ display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', padding:40, background:'#fcfbfa', position:'relative' }}>
        <svg style={{ position:'absolute', width:'100%', height:'100%', top:0, left:0, pointerEvents:'none', opacity:0.12 }} viewBox="0 0 800 600" fill="none">
          <path d="M-50,450 L120,380 L280,510 L450,220 L620,340 L850,110" stroke="#42000B" strokeWidth="2" strokeLinecap="round" />
        </svg>
        <div style={{ background:'#fff', borderRadius:16, padding:40, width:'100%', maxWidth:400, boxShadow:'0 10px 40px rgba(66,0,11,0.06)', border:'1px solid rgba(66,0,11,0.04)', position:'relative', zIndex:2 }}>
          {renderCard()}
        </div>
        <div style={{ position:'absolute', bottom:24, fontSize:12, color:'#999', zIndex:2 }}>Pashupati Capital Services Pvt. Ltd.</div>
      </div>
    </div>
  );
}